import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import ReactMarkdown from "react-markdown";
import rehypeRaw from "rehype-raw";
import { Col } from "reactstrap"
import "../../../assets/css/gamesCss/Tree/avlDeletion.css"
import "../../../assets/css/GameField.css"
import GameOverModal from "../../../components/GameOverModal";
import GameWonModal from "../../../components/GameWonModal";
import { useDarkMode } from "../../../context/DarkModeProvider";
import { useLanguage } from "context/LanguageProvider";

const AVLDeletionGame = () => {
    const [currentStage, setCurrentStage] = useState(1);
    const [treeNodes, setTreeNodes] = useState([]);
    const [availableNodes, setAvailableNodes] = useState([]);
    const [draggedNode, setDraggedNode] = useState(null);
    const [message, setMessage] = useState("");
    const [gameState, setGameState] = useState("playing");
    const [lives, setLives] = useState(3);
    const [deletedNode, setDeletedNode] = useState(null);
    const [deletedPosition, setDeletedPosition] = useState(null);
    const [showGameOver, setShowGameOver] = useState(false);
    const [showGameWon, setShowGameWon] = useState(false);
    const { darkMode } = useDarkMode();
    const { selLanguage, toggleLanguage, isLoading } = useLanguage();

    const handlePlayAgain = () => {
        resetGame();
    };

    const stages = {
        1: {
            description: selLanguage
                ? "Διαγράφηκαν  κόμβοι από το AVL tree. Αναδιατάξτε τους κόμβους για να επαναφέρετε την AVL ιδιότητα!"
                : "Some nodes have been deleted from the AVL tree. Rearrange the nodes to restore the AVL property!",
            // Δέντρο 3 επιπέδων (0-2) - 7 κόμβοι με 2 διαγραφές
            initialTreeWithGap: [50, null, 75, 10, 35, null, 90],
            deletedAtPosition: [5, 11],
            treeSize: 7
        },
        2: {
            description: selLanguage
                ? "Διαγράφηκαν  κόμβοι από το AVL tree. Αναδιατάξτε τους κόμβους για να επαναφέρετε την AVL ιδιότητα!"
                : "Some nodes have been deleted from the AVL tree. Rearrange the nodes to restore the AVL property!",
            initialTreeWithGap: [
                100,          // επίπεδο 0
                50, null,     // επίπεδο 1
                null, 75, 125, null, // επίπεδο 2
                12, 37, null, 87, 112, 137, 162, null // επίπεδο 3
            ],
            deletedAtPosition: [9, 19, 21],
            treeSize: 15
        },
        3: {
            description: selLanguage
                ? "Διαγράφηκαν  κόμβοι από το AVL tree. Αναδιατάξτε τους κόμβους για να επαναφέρετε την AVL ιδιότητα!"
                : "Some nodes have been deleted from the AVL tree. Rearrange the nodes to restore the AVL property!",
            // Δέντρο 4 επιπέδων (0-3) - 15 κόμβοι με 5 διαγραφές
            initialTreeWithGap: [
                100,         // επίπεδο 0
                null, 150,   // επίπεδο 1
                null, 75, 125, 175, // επίπεδο 2
                12, null, null, 87, null, null, null, 187 // επίπεδο 3 - 5 κενές θέσεις
            ],
            deletedAtPosition: [9, 12, 19, 21],
            treeSize: 15
        }
    };


    useEffect(() => {
        initializeStage(currentStage);
    }, [currentStage]);

    const initializeStage = (stage) => {
        const stageData = stages[stage];
        setTreeNodes([...stageData.initialTreeWithGap]);
        setAvailableNodes([]);
        setDeletedNode(stageData.deletedNodeValue);
        setDeletedPosition(stageData.deletedAtPosition);

        setGameState("playing");
    };

    const handleDragStart = (e, node, fromIndex) => {
        setDraggedNode({ node, fromIndex });
        e.dataTransfer.setData("text/plain", JSON.stringify({ node, fromIndex }));
    };

    const handleDragOver = (e) => {
        e.preventDefault();
    };

    const handleDropOnNode = (toIndex) => (e) => {
        e.preventDefault();
        const data = JSON.parse(e.dataTransfer.getData("text/plain"));
        const { node, fromIndex } = data;

        if (fromIndex === toIndex) return;

        setTreeNodes((prev) => {
            const updated = [...prev];

            if (updated[toIndex] === null) {
                updated[toIndex] = node;
                updated[fromIndex] = null;
            } else {
                const temp = updated[toIndex];
                updated[toIndex] = node;
                updated[fromIndex] = temp;
            }

            return updated;
        });
    };

    const handleDropOnAvailable = (e) => {
        e.preventDefault();
        const data = JSON.parse(e.dataTransfer.getData("text/plain"));
        const { node, fromIndex } = data;

        setTreeNodes((prev) => {
            const updated = [...prev];
            updated[fromIndex] = null;
            return updated;
        });

        setAvailableNodes((prev) => [...prev, node]);
    };

    const handleDragStartFromAvailable = (e, node) => {
        setDraggedNode({ node, fromIndex: 'available' });
        e.dataTransfer.setData("text/plain", JSON.stringify({ node, fromIndex: 'available' }));
    };

    const handleDropFromAvailable = (toIndex) => (e) => {
        e.preventDefault();
        const data = JSON.parse(e.dataTransfer.getData("text/plain"));
        const { node } = data;

        if (treeNodes[toIndex] !== null) return;

        setTreeNodes((prev) => {
            const updated = [...prev];
            updated[toIndex] = node;
            return updated;
        });

        setAvailableNodes((prev) => prev.filter((n) => n !== node));
    };

    // Έλεγχος αν το δέντρο είναι έγκυρο Binary Search Tree
    const isBST = (tree) => {
        const checkBSTRecursive = (index, min, max) => {
            if (index >= tree.length || tree[index] === null) {
                return true;
            }

            const value = tree[index];

            if (value <= min || value >= max) {
                return false;
            }

            const leftChild = 2 * index + 1;
            const rightChild = 2 * index + 2;

            return checkBSTRecursive(leftChild, min, value) &&
                checkBSTRecursive(rightChild, value, max);
        };

        return checkBSTRecursive(0, -Infinity, Infinity);
    };

    // Υπολογισμός ύψους υποδέντρου
    const getHeight = (tree, index) => {
        if (index >= tree.length || tree[index] === null) {
            return 0;
        }

        const leftHeight = getHeight(tree, 2 * index + 1);
        const rightHeight = getHeight(tree, 2 * index + 2);

        return 1 + Math.max(leftHeight, rightHeight);
    };

    // Έλεγχος Balance Factor για κάθε κόμβο
    const isBalanced = (tree) => {
        const checkBalance = (index) => {
            if (index >= tree.length || tree[index] === null) {
                return true;
            }

            const leftHeight = getHeight(tree, 2 * index + 1);
            const rightHeight = getHeight(tree, 2 * index + 2);
            const balanceFactor = leftHeight - rightHeight;

            // Το Balance Factor πρέπει να είναι -1, 0, ή 1
            if (Math.abs(balanceFactor) > 1) {
                return false;
            }

            const leftChild = 2 * index + 1;
            const rightChild = 2 * index + 2;

            return checkBalance(leftChild) && checkBalance(rightChild);
        };

        return checkBalance(0);
    };

    // Έλεγχος αν όλοι οι κόμβοι έχουν τοποθετηθεί
    const allNodesPlaced = (tree) => {
        const deletedPositions = stages[currentStage].deletedAtPosition;

        for (let i = 0; i < tree.length; i++) {
            // Αν η θέση δεν είναι διαγραμμένη και είναι null, επιστρέφει false
            if (!deletedPositions.includes(i) && tree[i] === null) {
                // Ελέγχουμε αν η θέση πρέπει να έχει κόμβο (ο γονέας υπάρχει)
                const parentIndex = Math.floor((i - 1) / 2);
                if (parentIndex >= 0 && tree[parentIndex] !== null) {
                    // Η θέση θα μπορούσε να έχει κόμβο, αλλά δεν είναι υποχρεωτικό
                    continue;
                }
            }
        }
        return true;
    };

    const checkSolution = () => {
        // Έλεγχος 1: Είναι έγκυρο BST;
        if (!isBST(treeNodes)) {
            const newLives = lives - 1;
            setLives(newLives);

            if (newLives <= 0) {
                setGameState("lost");
                setShowGameOver(true);
                setMessage(selLanguage ? "Έχασες όλες τις ζωές σου. Καλή προσπάθεια!" : "You have lost all your lives. Better luck next time!");
            } else {
                setMessage(selLanguage ? `Δεν είναι έγκυρο Binary Search Tree! Οι κόμβοι αριστερά πρέπει να είναι μικρότεροι και δεξιά μεγαλύτεροι. Απομένουν ${newLives} ζωές.` : `Not a valid Binary Search Tree! Left nodes must be smaller and right nodes larger. ${newLives} lives remaining.`);
            }
            return;
        }

        // Έλεγχος 2: Είναι ισορροπημένο AVL;
        if (!isBalanced(treeNodes)) {
            const newLives = lives - 1;
            setLives(newLives);

            if (newLives <= 0) {
                setGameState("lost");
                setShowGameOver(true);
                setMessage(selLanguage ? " Έχασες όλες τις ζωές σου. Καλή προσπάθεια!" : "You have lost all your lives. Better luck next time!");
            } else {
                setMessage(selLanguage ? ` Το δέντρο δεν είναι ισορροπημένο! Το Balance Factor κάποιου κόμβου ξεπερνά το 1. Απομένουν ${newLives} ζωές.` : ` The tree is not balanced! Some node's Balance Factor exceeds 1. ${newLives} lives remaining.`);
            }
            return;
        }

        // Αν όλοι οι έλεγχοι πέρασαν επιτυχώς
        if (currentStage === 3) {
            setGameState("completed");
            setShowGameWon(true);
            setMessage(selLanguage ? "Συγχαρητήρια! Ολοκληρώσατε όλα τα στάδια!" : "Congratulations! You have completed all stages!");
        } else {
            setMessage(selLanguage ? `Σωστά! Το δέντρο είναι έγκυρο BST και ισορροπημένο AVL! Προχωρώντας στο στάδιο ${currentStage + 1}` : `Correct! The tree is a valid BST and balanced AVL! Proceeding to stage ${currentStage + 1}`);
            setTimeout(() => {
                setCurrentStage(currentStage + 1);
            }, 2000);
        }
    };

    const resetGame = () => {
        setCurrentStage(1);
        setLives(3);
        setGameState("playing");
        setMessage("");
        setShowGameOver(false);
        setShowGameWon(false);
        initializeStage(1);
    };

    // Υπολογίζει την θέση των κόμβων για κάθε επίπεδο
    const getNodePositions = (level, nodeIndex, totalLevels) => {
        const baseSpacing = 600;
        const levelSpacing = baseSpacing / Math.pow(2, level);
        const nodesInLevel = Math.pow(2, level);
        const indexInLevel = nodeIndex - (Math.pow(2, level) - 1);
        const startOffset = -(nodesInLevel - 1) * levelSpacing / 2;

        return {
            x: startOffset + indexInLevel * levelSpacing,
            y: level * 100
        };
    };

    // Υπολογίζει ποιο επίπεδο ανήκει ένας κόμβος
    const getNodeLevel = (index) => {
        return Math.floor(Math.log2(index + 1));
    };

    // Δημιουργεί τις ακμές του δέντρου
    // Δημιουργεί τις ακμές του δέντρου
    const renderEdges = () => {
        const edges = [];
        const tree = treeNodes;

        for (let i = 0; i < tree.length; i++) {
            if (tree[i] === null) continue;

            const leftChildIndex = 2 * i + 1;
            const rightChildIndex = 2 * i + 2;

            const parentLevel = getNodeLevel(i);
            const parentPos = getNodePositions(parentLevel, i, 5);

            // Αριστερό παιδί (ακόμα και αν είναι null)
            if (leftChildIndex < tree.length) {
                const childLevel = getNodeLevel(leftChildIndex);
                const childPos = getNodePositions(childLevel, leftChildIndex, 5);

                edges.push(
                    <line
                        key={`edge-${i}-${leftChildIndex}`}
                        x1={parentPos.x}
                        y1={parentPos.y + 25}
                        x2={childPos.x}
                        y2={childPos.y + 25}
                        stroke={tree[leftChildIndex] === null ? "#94a3b8" : "#64748b"}
                        strokeWidth="2"
                        opacity={tree[leftChildIndex] === null ? "0.3" : "0.6"}
                        strokeDasharray={tree[leftChildIndex] === null ? "5,5" : "0"}
                    />
                );
            }

            // Δεξί παιδί (ακόμα και αν είναι null)
            if (rightChildIndex < tree.length) {
                const childLevel = getNodeLevel(rightChildIndex);
                const childPos = getNodePositions(childLevel, rightChildIndex, 5);

                edges.push(
                    <line
                        key={`edge-${i}-${rightChildIndex}`}
                        x1={parentPos.x}
                        y1={parentPos.y + 25}
                        x2={childPos.x}
                        y2={childPos.y + 25}
                        stroke={tree[rightChildIndex] === null ? "#94a3b8" : "#64748b"}
                        strokeWidth="2"
                        opacity={tree[rightChildIndex] === null ? "0.3" : "0.6"}
                        strokeDasharray={tree[rightChildIndex] === null ? "5,5" : "0"}
                    />
                );
            }
        }

        return edges;
    };

    const renderNode = (nodeValue, index) => {
        const isDeletedPosition = deletedPosition && deletedPosition.includes(index);
        const level = getNodeLevel(index);
        const position = getNodePositions(level, index, 5);

        return (
            <g key={index}>
                <foreignObject
                    x={position.x - 25}
                    y={position.y}
                    width="50"
                    height="50"
                    onDragOver={handleDragOver}
                    onDrop={(e) => {
                        if (draggedNode?.fromIndex === 'available') {
                            handleDropFromAvailable(index)(e);
                        } else {
                            handleDropOnNode(index)(e);
                        }
                    }}
                >
                    <div
                        draggable={nodeValue !== null}
                        onDragStart={(e) => nodeValue && handleDragStart(e, nodeValue, index)}
                        style={{
                            width: "50px",
                            height: "50px",
                            borderRadius: "50%",
                            backgroundColor: nodeValue !== null
                                ? "#3b82f6"
                                : isDeletedPosition
                                    ? "#ef4444"
                                    : "#e5e7eb",
                            color: "white",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontWeight: "bold",
                            fontSize: "14px",
                            cursor: nodeValue !== null ? "grab" : "default",
                            border: nodeValue !== null
                                ? "2px solid #1d4ed8"
                                : isDeletedPosition
                                    ? "2px solid #dc2626"
                                    : "2px dashed #9ca3af",
                            transition: "all 0.3s ease",
                            filter: nodeValue !== null
                                ? "drop-shadow(0 6px 15px rgba(59, 130, 246, 0.4))"
                                : "drop-shadow(0 4px 8px rgba(0,0,0,0.15))"
                        }}
                        title={nodeValue ? selLanguage ? "Σύρε για μετακίνηση" : "Drag to move"
                            : isDeletedPosition ? selLanguage ? "Διαγραμμένη θέση" : "Deleted position"
                                : selLanguage ? "Κενή θέση" : "Empty position"}
                    >
                        {nodeValue || (isDeletedPosition ? "✗" : "")}
                    </div>
                </foreignObject>
            </g>
        );
    };

    const renderTree = () => {
        const tree = treeNodes;
        const maxLevel = Math.floor(Math.log2(tree.length));
        const svgWidth = 800;
        const svgHeight = (maxLevel + 2) * 80 + 100;

        return (
            <div style={{
                display: "flex",
                justifyContent: "center",
                width: "100%",
                overflowX: "auto",
                marginTop: "30px"
            }}>
                <svg
                    width={svgWidth}
                    height={svgHeight}
                    viewBox={`${-svgWidth / 2} 0 ${svgWidth} ${svgHeight}`}
                    style={{ minWidth: "600px" }}
                >
                    {/* Ακμές πρώτα (πίσω από τους κόμβους) */}
                    {renderEdges()}

                    {/* Κόμβοι */}
                    {tree.map((nodeValue, index) => renderNode(nodeValue, index))}
                </svg>
            </div>
        );
    };

    useEffect(() => {
        if (message && !message.includes(selLanguage ? "Διαγράφηκε" : "Deleted")) {
            const timer = setTimeout(() => {
                setMessage("");
            }, 3000);
            return () => clearTimeout(timer);
        }
    }, [message]);

    return (
        <div
            className='main-content avlDeletionContainer'
            style={{
                background: darkMode ? "#1E293B" : "var(--customBuildButton)",
            }}
        >
            <GameOverModal
                isVisible={showGameOver}
                onPlayAgain={handlePlayAgain}
            />

            <GameWonModal
                isVisible={showGameWon}
                onPlayAgain={handlePlayAgain}
            />

            <Col xl="12" lg="12" md="12" sm="12" xs="12">
                <div className="max-w-7xl mx-auto" style={{ width: "100%" }}>

                    {/* Lives και Stage Info */}
                    <div style={{
                        width: "100%",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        marginBottom: "15px",
                        flexWrap: "wrap",
                        gap: "10px"
                    }}>
                        {/* Stage Info */}
                        <div style={{
                            color: darkMode ? "white" : "#1e293b",
                            padding: "12px 24px",
                            borderRadius: "12px",
                            fontWeight: "700",
                            fontSize: "18px",
                            border: darkMode ? "2px solid rgba(255, 255, 255, 0.2)" : "2px solid #1e293b",
                            display: "flex",
                            alignItems: "center",
                            gap: "10px"
                        }}>
                            {selLanguage ? `Επίπεδο ${currentStage}/3` : `Stage ${currentStage}/3`}
                        </div>

                        {/* Lives */}
                        <div style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "12px"
                        }}>
                            <div style={{ display: "flex", gap: "6px" }}>
                                {[1, 2, 3].map((heartNum) => (
                                    <div
                                        key={heartNum}
                                        style={{
                                            fontSize: "24px",
                                            filter: lives >= heartNum
                                                ? "drop-shadow(0 4px 8px rgba(220, 38, 38, 0.4))"
                                                : "grayscale(100%) opacity(0.3)",
                                        }}
                                    >
                                        ❤️
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    <br />

                    {/* Description */}
                    <div className="avlDeletionPron">
                        <ReactMarkdown rehypePlugins={[rehypeRaw]}>
                            {stages[currentStage].description}
                        </ReactMarkdown>
                    </div>

                    {/* Tree Visualization */}
                    <div className="avlDeletionVisual">
                        {renderTree()}
                    </div>

                    {/* Available Nodes */}
                    {availableNodes.length > 0 && (
                        <div
                            style={{
                                marginBottom: "24px",
                                background: "#8fb2c280",
                                padding: "20px 24px",
                                borderRadius: "16px",
                                border: "1px solid rgba(148, 163, 184, 0.2)",
                                boxShadow: "0 10px 25px rgba(0, 0, 0, 0.2)",
                                maxWidth: "600px",
                                margin: "0 auto 24px auto",
                                width: "100%"
                            }}
                            onDragOver={handleDragOver}
                            onDrop={handleDropOnAvailable}
                        >
                            <h3 style={{
                                fontWeight: "600",
                                marginBottom: "16px",
                                color: "white",
                                textAlign: "center"
                            }}>
                                {selLanguage ? "Προσωρινά αποθηκευμένοι κόμβοι:" : "Temporarily Stored Nodes:"}
                            </h3>
                            <div style={{
                                display: "flex",
                                gap: "12px",
                                flexWrap: "wrap",
                                justifyContent: "center"
                            }}>
                                {availableNodes.map((node, index) => (
                                    <motion.div
                                        className="avlDeletionNodes"
                                        key={`${node}-${index}`}
                                        draggable
                                        onDragStart={(e) => handleDragStartFromAvailable(e, node)}
                                        whileHover={{ scale: 1.1 }}
                                        whileTap={{ scale: 0.95 }}
                                    >
                                        {node}
                                    </motion.div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Messages */}
                    <AnimatePresence>
                        {message && (
                            <motion.div
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -10 }}
                                style={{
                                    textAlign: "center",
                                    padding: "20px",
                                    borderRadius: "16px",
                                    marginBottom: "20px",
                                    background: message.includes(selLanguage ? "Σωστά" : "Correct") ||
                                        message.includes(selLanguage ? "Συγχαρητήρια" : "Congratulations")
                                        ? "linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%)"
                                        : message.includes(selLanguage ? "Δεν είναι" : "Not a valid") ||
                                            message.includes(selLanguage ? "δεν είναι" : "not balanced") ||
                                            message.includes(selLanguage ? "Έχασες" : "lost")
                                            ? "linear-gradient(135deg, #fef2f2 0%, #fecaca 100%)"
                                            : "linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)",
                                    border: `2px solid ${message.includes(selLanguage ? "Σωστά" : "Correct") ||
                                        message.includes(selLanguage ? "Συγχαρητήρια" : "Congratulations")
                                        ? "#10b981" :
                                        message.includes(selLanguage ? "Δεν είναι" : "Not a valid") ||
                                            message.includes(selLanguage ? "δεν είναι" : "not balanced") ||
                                            message.includes(selLanguage ? "Έχασες" : "lost")
                                            ? "#ef4444" : "#3b82f6"
                                        }`,
                                    color: message.includes(selLanguage ? "Σωστά" : "Correct") ||
                                        message.includes(selLanguage ? "Συγχαρητήρια" : "Congratulations")
                                        ? "#059669" :
                                        message.includes(selLanguage ? "Δεν είναι" : "Not a valid") ||
                                            message.includes(selLanguage ? "δεν είναι" : "not balanced") ||
                                            message.includes(selLanguage ? "Έχασες" : "lost")
                                            ? "#dc2626" : "#1d4ed8",
                                    fontWeight: 600,
                                    fontSize: "1rem",
                                    maxWidth: "600px",
                                    margin: "0 auto 20px auto",
                                    width: "100%"
                                }}
                            >
                                {message}
                            </motion.div>
                        )}
                    </AnimatePresence>

                    {/* Buttons */}
                    <div className="avlButtonWrapper">
                        <motion.button
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            onClick={checkSolution}
                            disabled={gameState !== "playing"}
                            style={{
                                background: gameState === "playing"
                                    ? "linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)"
                                    : "linear-gradient(135deg, #6b7280 0%, #4b5563 100%)",
                                color: "white",
                                border: "none",
                                borderRadius: "16px",
                                padding: "14px 28px",
                                fontSize: "1rem",
                                fontWeight: 600,
                                cursor: gameState === "playing" ? "pointer" : "not-allowed",
                                boxShadow: "0 10px 25px rgba(59, 130, 246, 0.3)",
                                transition: "all 0.3s ease",
                                flex: 1,
                                maxWidth: "150px"
                            }}
                        >
                            {selLanguage ? "Έλεγχος" : "Check"}
                        </motion.button>

                        <motion.button
                            className="avlDeletionRestart"
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            onClick={() => initializeStage(currentStage)}
                        >
                            {selLanguage ? "Επαναφορά" : "Reset"}
                        </motion.button>

                        {(gameState === "lost" || gameState === "completed") && (
                            <motion.button
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.95 }}
                                onClick={resetGame}
                                style={{
                                    background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
                                    color: "white",
                                    border: "none",
                                    borderRadius: "16px",
                                    padding: "14px 28px",
                                    fontSize: "1rem",
                                    fontWeight: 600,
                                    cursor: "pointer",
                                    boxShadow: "0 10px 25px rgba(16, 185, 129, 0.3)",
                                    transition: "all 0.3s ease",
                                    flex: 1,
                                    maxWidth: "150px"
                                }}
                            >
                                {selLanguage ? "Νέο Παιχνίδι" : "New Game"}
                            </motion.button>
                        )}
                    </div>
                </div>
            </Col>
        </div>
    );
};

export default AVLDeletionGame;